import {
  AutocompleteNoOptions,
  FormField,
  SwitchField,
} from '@dev-spendesk/grapes';
import { useState } from 'react';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { TripBudget } from 'modules/budgets/components/BudgetOverview/TripBudget/TripBudget';
import {
  type Trip,
  useCurrentTripsQuery,
} from 'modules/requests/hooks/useCurrentTripsQuery';
import { getTripDates } from 'modules/requests/utils/trips';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { UnexpectedErrorCallout } from 'src/core/common/components/UnexpectedErrorCallout/UnexpectedErrorCallout';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import FEATURES from 'src/core/constants/features';

import { TripSelect } from './TripSelect';

type Props = {
  className?: string;
  selectedTripId?: string;
  setSelectedTripId: (id: string | undefined) => void;
  showBudgetBreakdown?: boolean;
  impersonatedUserId?: string;
};

type TripOption = Omit<Trip, 'budget'> & {
  key: string;
  label: string;
  budget: Required<NonNullable<Trip['budget']>> & {
    state: NonNullable<NonNullable<Trip['budget']>['state']>;
  };
};

export const TripSelector = ({
  className,
  selectedTripId,
  setSelectedTripId,
  showBudgetBreakdown,
  impersonatedUserId,
}: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const hasTravelBudgetRequestsFeature = useFeature(
    FEATURES.TRAVEL_BUDGET_REQUEST,
  );

  const tripsQuery = useCurrentTripsQuery({
    isEnabled: hasTravelBudgetRequestsFeature,
    // add impersonatedUserId when support for delegators is added or when viewing a request that belongs to another user
    impersonatedUserId,
  });
  const trips = unwrapQuery(tripsQuery)?.data.trips;

  const [isTripSectionOpen, setIsTripSectionOpen] = useState(!!selectedTripId);
  const tripOptions = (trips ?? [])
    .filter((trip): trip is TripOption => !!trip.budget?.state)
    .map((trip) => ({
      ...trip,
      key: trip.id,
      label: `${trip.name ?? '-'} | ${getTripDates(new Date(trip.startDate), new Date(trip.endDate), localeFormat)}`,
    }));

  const currentTrip = tripOptions.find((trip) => trip.key === selectedTripId);

  const renderNoOptions = () => {
    if (impersonatedUserId && !tripsQuery.isError) {
      return (
        <AutocompleteNoOptions variant="info" className="body-m text-center">
          <p className="text-content-primary body-m mb-4 font-semibold">
            {t('requests.panel.travelBudget.tripSelect.impersonatedEmptyTitle')}
          </p>
          <p className="text-content-secondary-bg-secondary body-s">
            {t('requests.panel.travelBudget.tripSelect.impersonatedEmptyHint')}
          </p>
        </AutocompleteNoOptions>
      );
    }
    return (
      <AutocompleteNoOptions
        variant={tripsQuery.isError ? 'warning' : 'info'}
        className="body-m text-center"
      >
        <p className="text-content-primary body-m mb-4 font-semibold">
          {tripsQuery.isError
            ? t('requests.panel.travelBudget.tripSelect.errorTitle')
            : t('requests.panel.travelBudget.tripSelect.emptyTitle')}
        </p>
        <p className="text-content-secondary-bg-secondary body-s">
          {tripsQuery.isError
            ? t('misc.errorState.subtitle')
            : t('requests.panel.travelBudget.tripSelect.emptyHint')}
        </p>
      </AutocompleteNoOptions>
    );
  };

  if (!hasTravelBudgetRequestsFeature) {
    return null;
  }

  return (
    <div className={className}>
      <SwitchField
        fit="parent"
        className="mb-24"
        label={t('requests.panel.travelBudget.newLinkLabel')}
        isChecked={isTripSectionOpen}
        onChange={() => {
          if (isTripSectionOpen) {
            setSelectedTripId(undefined);
          } else if (!isTripSectionOpen && tripOptions.length > 0) {
            setSelectedTripId(tripOptions[0].key);
          }
          setIsTripSectionOpen(!isTripSectionOpen);
        }}
      />
      <ErrorBoundary
        context={{
          team: 'travel-and-expenses',
          scope: 'requestsTripSelector',
        }}
        fallbackComponent={isTripSectionOpen && <UnexpectedErrorCallout />}
      >
        {isTripSectionOpen && (
          <FormField
            className="mb-8"
            label={t('requests.panel.travelBudget.tripSelect.title')}
          >
            <TripSelect
              options={tripOptions.map((trip) => ({
                key: trip.key,
                label: trip.label,
                name: trip.name,
                dates: {
                  from: new Date(trip.startDate),
                  to: new Date(trip.endDate),
                },
                budget: trip.budget,
              }))}
              value={
                currentTrip && {
                  key: currentTrip.key,
                  label: currentTrip.label,
                  name: currentTrip.name,
                  budget: currentTrip.budget,
                  dates: {
                    from: new Date(currentTrip.startDate),
                    to: new Date(currentTrip.endDate),
                  },
                }
              }
              onSelect={(trip) => setSelectedTripId(trip.key)}
              renderNoOptions={renderNoOptions}
            />
          </FormField>
        )}

        {showBudgetBreakdown && currentTrip && (
          <TripBudget trip={currentTrip} />
        )}
      </ErrorBoundary>
    </div>
  );
};
